import React from 'react';
import '../styles/ProjectServiceScope.css';

/**
 * Operational tasks of one service for a new project (from Ritika's Create Project):
 * groups on the left, the active group's tasks on the right. Edits stay on the
 * project and do not change Service Management.
 * scope = { tasks: [{ id, title, groupName, scope, instructions }], activeGroup, extraGroups, newGroupName }
 */
function groupsOfScope(tasks, extras = []) {
  const map = new Map();
  (tasks || []).forEach((task) => {
    const name = String(task.groupName || 'Tasks').trim() || 'Tasks';
    if (!map.has(name)) map.set(name, []);
    map.get(name).push(task);
  });
  (extras || []).forEach((name) => {
    const label = String(name || '').trim();
    if (label && !map.has(label)) map.set(label, []);
  });
  if (!map.size) map.set('Tasks', []);
  return [...map.entries()].map(([name, rows]) => ({ name, tasks: rows }));
}

export default function ProjectServiceScope({ scope, onChange }) {
  if (!scope) return null;
  const groups = groupsOfScope(scope.tasks, scope.extraGroups);
  const activeName = groups.some((group) => group.name === scope.activeGroup)
    ? scope.activeGroup
    : groups[0]?.name || 'Tasks';
  const activeTasks = groups.find((group) => group.name === activeName)?.tasks || [];

  const updateTask = (taskId, field, value) => {
    onChange({
      ...scope,
      tasks: (scope.tasks || []).map((task) =>
        task.id === taskId ? { ...task, [field]: value } : task,
      ),
    });
  };

  const addGroup = () => {
    const name = String(scope.newGroupName || '').trim();
    if (!name) return;
    onChange({
      ...scope,
      newGroupName: '',
      activeGroup: name,
      extraGroups: [...new Set([...(scope.extraGroups || []), name])],
    });
  };

  return (
    <div className="pm-scope">
      <div className="pm-scope-head">
        <div>
          <strong>Operational tasks</strong>
          <span>
            Copied for this client. Edits stay on the project and do not change Service Management.
          </span>
        </div>
      </div>
      <div className="pm-scope-board">
        <aside className="pm-scope-groups">
          <header>
            <span>Groups</span>
            <strong>{groups.length}</strong>
          </header>
          {groups.map((group) => (
            <button
              key={group.name}
              type="button"
              className={group.name === activeName ? 'is-on' : ''}
              onClick={() => onChange({ ...scope, activeGroup: group.name })}
            >
              <b>{group.name}</b>
              <small>{group.tasks.length} tasks</small>
            </button>
          ))}
          <div className="pm-scope-new-group">
            <input
              value={scope.newGroupName || ''}
              placeholder="New group for this project"
              onChange={(e) => onChange({ ...scope, newGroupName: e.target.value })}
              onKeyDown={(e) => {
                if (e.key !== 'Enter') return;
                e.preventDefault();
                addGroup();
              }}
            />
            <button type="button" onClick={addGroup}>
              Add group
            </button>
          </div>
        </aside>
        <section className="pm-scope-tasks">
          <header>
            <div>
              <h4>{activeName}</h4>
              <span>{activeTasks.length} tasks</span>
            </div>
            <button
              type="button"
              onClick={() =>
                onChange({
                  ...scope,
                  activeGroup: activeName,
                  tasks: [
                    ...(scope.tasks || []),
                    {
                      id: `project-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
                      title: '',
                      groupName: activeName,
                      scope: '',
                      instructions: '',
                    },
                  ],
                })
              }
            >
              ＋ Add task
            </button>
          </header>
          {activeTasks.length ? (
            activeTasks.map((task) => (
              <article className="pm-scope-task" key={task.id}>
                <div className="pm-scope-task-top">
                  <label>
                    Task name
                    <input
                      value={task.title || ''}
                      placeholder="Task name"
                      onChange={(e) => updateTask(task.id, 'title', e.target.value)}
                    />
                  </label>
                  <button
                    type="button"
                    aria-label="Remove task"
                    onClick={() =>
                      onChange({
                        ...scope,
                        tasks: (scope.tasks || []).filter((row) => row.id !== task.id),
                      })
                    }
                  >
                    Remove
                  </button>
                </div>
                <label>
                  Client-facing scope
                  <textarea
                    rows={3}
                    value={task.scope || ''}
                    onChange={(e) => updateTask(task.id, 'scope', e.target.value)}
                  />
                </label>
                <label>
                  Internal work instructions
                  <textarea
                    rows={3}
                    value={task.instructions || ''}
                    onChange={(e) => updateTask(task.id, 'instructions', e.target.value)}
                  />
                </label>
              </article>
            ))
          ) : (
            <p className="csp-empty">No tasks in this group yet.</p>
          )}
        </section>
      </div>
    </div>
  );
}
